import { useQueryClient } from '@tanstack/react-query';
import { toast } from '@tempo-io/sui-sonner';
import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { ItemsApi } from '@/lib/api/common/items';
import { commonQueryKeys } from '@/lib/query-keys/common';
import { bumpVisibleItemsCache } from '../context/VisibleItemsContext';

export const useItemReparent = (instanceKey: string) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  return React.useCallback(
    async (
      itemIds: number[],
      newParentId: number | null,
      relativePriority?: { id: number; type: 'before' | 'after' },
    ) => {
      if (itemIds.length === 0) return;
      if (itemIds.length === 1) {
        const [itemId] = itemIds;
        if (itemId === undefined) return;
        try {
          await ItemsApi.updateItem({
            itemId,
            data: {
              parentId: newParentId,
              ...(relativePriority !== undefined && { relativePriority }),
            },
          });
        } catch {
          toast.error(
            t(($) => $.common.errors.global.title),
            {
              description: t(($) => $.common.errors.genericWithSupport),
            },
          );
          return;
        }
      } else {
        let failedCount = 0;
        try {
          const response = await ItemsApi.bulkPartialPatchItems({
            itemIds,
            fields: {
              parentId: newParentId,
              ...(relativePriority !== undefined && { relativePriority }),
            },
          });
          failedCount = response.failed.length;
        } catch {
          toast.error(
            t(($) => $.common.errors.global.title),
            {
              description: t(($) => $.common.errors.genericWithSupport),
            },
          );
          return;
        }
        if (failedCount > 0) {
          toast.error(
            t(($) => $.common.errors.global.title),
            {
              description: t(($) => $.common.errors.genericWithSupport),
            },
          );
          if (failedCount === itemIds.length) return;
        }
      }
      // Await the refetch so the tree rebuilds before we bump the visible-items
      // cache. Both state updates land in the same React render batch, preventing
      // the skeleton flash that would otherwise appear while the grid waits for
      // updateRows to re-run.
      await queryClient.refetchQueries({ queryKey: commonQueryKeys.itemsTreeSet.all() });
      bumpVisibleItemsCache(queryClient, instanceKey);
    },
    [queryClient, t, instanceKey],
  );
};
